
import type { PerformanceData } from '../types';

export interface FunnelStep {
  name: string;
  count: number;
  dropoffRate: number;
}

/**
 * Analyzes quiz completion rates at different stages to identify dropoff points
 * @param performanceData Array of quiz performance data
 * @returns Array of funnel steps with counts and dropoff rates
 */
export const analyzeFunnelData = (performanceData: PerformanceData[]): FunnelStep[] => {
  if (!performanceData?.length) return [];
  
  // Count total quizzes started
  const totalStarted = performanceData.length;
  
  // Count quizzes with at least 25% completion
  const partiallyCompleted = performanceData.filter(quiz => 
    (quiz.correct_answers / quiz.total_questions) >= 0.25
  ).length;
  
  // Count quizzes with at least 50% completion
  const halfwayCompleted = performanceData.filter(quiz => 
    (quiz.correct_answers / quiz.total_questions) >= 0.5
  ).length;
  
  // Count quizzes with at least 75% completion
  const mostlyCompleted = performanceData.filter(quiz => 
    (quiz.correct_answers / quiz.total_questions) >= 0.75
  ).length;
  
  // Count quizzes with 100% completion
  const fullyCompleted = performanceData.filter(quiz => 
    (quiz.correct_answers / quiz.total_questions) === 1
  ).length;

  // Calculate dropoff rates
  const calculateDropoffRate = (current: number, previous: number) => 
    previous > 0 ? Math.round(((previous - current) / previous) * 100) : 0;

  return [
    {
      name: 'Started',
      count: totalStarted,
      dropoffRate: 0
    },
    {
      name: '25% Complete',
      count: partiallyCompleted,
      dropoffRate: calculateDropoffRate(partiallyCompleted, totalStarted)
    },
    {
      name: '50% Complete',
      count: halfwayCompleted,
      dropoffRate: calculateDropoffRate(halfwayCompleted, partiallyCompleted)
    },
    {
      name: '75% Complete',
      count: mostlyCompleted,
      dropoffRate: calculateDropoffRate(mostlyCompleted, halfwayCompleted)
    },
    {
      name: 'Fully Complete',
      count: fullyCompleted,
      dropoffRate: calculateDropoffRate(fullyCompleted, mostlyCompleted)
    }
  ];
};

/**
 * Identifies the stage with the highest dropoff rate
 * @param funnelData Array of funnel steps
 * @returns The funnel step with the highest dropoff rate, or null if none found
 */
export const identifyProblemStage = (funnelData: FunnelStep[]): FunnelStep | null => {
  if (!funnelData?.length) return null;
  
  return [...funnelData]
    .sort((a, b) => b.dropoffRate - a.dropoffRate)
    .find(step => step.dropoffRate > 0) || null;
};

/**
 * Provides insights based on funnel data analysis
 * @param funnelData Array of funnel steps
 * @returns Object containing insights about the funnel data
 */
export const generateFunnelInsights = (funnelData: FunnelStep[]): {
  highDropoffStage: FunnelStep | null;
  completionRate: number;
  insight: string;
  insightType: 'success' | 'warning' | 'danger' | 'info';
} => {
  if (!funnelData?.length) {
    return {
      highDropoffStage: null,
      completionRate: 0,
      insight: "Not enough data to generate insights.",
      insightType: 'info'
    };
  }

  const startedCount = funnelData[0].count;
  const completedCount = funnelData[funnelData.length - 1].count;
  const completionRate = startedCount > 0 
    ? Math.round((completedCount / startedCount) * 100) 
    : 0;

  const highDropoffStage = identifyProblemStage(funnelData);
  
  let insight = "";
  let insightType: 'success' | 'warning' | 'danger' | 'info' = 'info';
  
  if (completionRate >= 80) {
    insight = `Great job! ${completionRate}% of quizzes are fully completed.`;
    insightType = 'success';
  } else if (completionRate >= 50) {
    insight = `${completionRate}% completion rate. There's room for improvement.`;
    insightType = 'info';
  } else if (highDropoffStage && highDropoffStage.dropoffRate > 40) {
    insight = `High dropoff (${highDropoffStage.dropoffRate}%) at the ${highDropoffStage.name} stage. Consider reviewing difficulty or length.`;
    insightType = 'danger';
  } else if (highDropoffStage) {
    insight = `Moderate dropoff (${highDropoffStage.dropoffRate}%) at the ${highDropoffStage.name} stage.`;
    insightType = 'warning';
  } else {
    insight = `${completionRate}% overall completion rate.`;
    insightType = 'info';
  }
  
  return {
    highDropoffStage,
    completionRate,
    insight,
    insightType
  };
};
